import axios, { AxiosInstance, AxiosResponse, Method, AxiosError, AxiosRequestConfig } from 'axios'
import { emitAuthError } from './authEvents'

const API_BASE_URL = process.env.REACT_APP_BASE_URL_API || 'https://ioag-prod-api.azurewebsites.net/api'

// Constants
const REFRESH_TIMEOUT_MS = 10000
const MAX_QUEUE_SIZE = 50

// Flag to track if a token refresh is in progress
let isRefreshing = false
// Queue of failed requests to retry after token refresh
let failedQueue: Array<{
  resolve: (value?: unknown) => void
  reject: (error?: unknown) => void
  config: AxiosRequestConfig
}> = []

// Process the queue of failed requests
const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      // Update the authorization header with the new token
      if (token && prom.config.headers) {
        prom.config.headers.Authorization = `Bearer ${token}`
      }
      prom.resolve()
    }
  })
  failedQueue = []
}

// Error types
export interface ApiError {
  code: string
  message: string
  details?: unknown
  statusCode?: number
}

export class ApiClientError extends Error {
  constructor(public readonly error: ApiError, public readonly statusCode?: number) {
    super(error.message)
    this.name = 'ApiClientError'
  }
}

// Handle 401 errors with token refresh
async function handle401Error(
  error: AxiosError,
  instance: AxiosInstance,
  apiError: ApiClientError
): Promise<AxiosResponse | void> {
  const originalRequest = error.config
  const status = error.response?.status || 401

  // Check if this request has already been retried (prevent infinite loops)
  if (originalRequest && !(originalRequest as any)._retry) {
    // Mark request as retried
    ;(originalRequest as any)._retry = true

    if (isRefreshing) {
      // If refresh is in progress, check queue size limit
      if (failedQueue.length >= MAX_QUEUE_SIZE) {
        throw apiError
      }
      // Queue this request - when resolved, retry; when rejected, throw the original apiError
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject, config: originalRequest })
      }).then(
        () => instance(originalRequest),
        () => {
          // When refresh fails, throw the original API error, not the refresh error
          throw apiError
        }
      )
    }

    isRefreshing = true

    try {
      // Dynamically import to avoid circular dependency with timeout
      const refreshWithTimeout = Promise.race([
        import('../services/auth').then(({ refreshSession }) => refreshSession()),
        new Promise<null>((_, reject) =>
          setTimeout(() => reject(new Error('Token refresh timeout')), REFRESH_TIMEOUT_MS)
        ),
      ])

      const session = await refreshWithTimeout

      if (session?.accessToken) {
        // Refresh successful - update token and retry
        localStorage.setItem('token', session.accessToken)
        isRefreshing = false
        processQueue(null, session.accessToken)

        // Update the original request with new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${session.accessToken}`
        }
        return instance(originalRequest)
      } else {
        // Refresh returned no session - user needs to re-login
        throw new Error('Session refresh failed')
      }
    } catch (err) {
      // Refresh failed - clear token and emit auth error
      const refreshError = err instanceof Error ? err : new Error(String(err))
      isRefreshing = false
      processQueue(refreshError, null)
      localStorage.removeItem('token')
      emitAuthError('auth:unauthorized', {
        statusCode: status,
        message: apiError.error.message,
        url: error.config?.url,
      })
      throw apiError
    }
  }

  // Request was already retried or has no config - emit auth error and throw
  localStorage.removeItem('token')
  emitAuthError('auth:unauthorized', {
    statusCode: status,
    message: apiError.error.message,
    url: error.config?.url,
  })
  throw apiError
}

// Token validation helper
function getValidToken(): string | null {
  try {
    const token = localStorage.getItem('token')

    if (!token || token === 'undefined' || token === 'null') {
      if (token === 'undefined') {
        console.warn('⚠️ Found invalid token "undefined" in localStorage, clearing it')
        localStorage.removeItem('token')
      }
      return null
    }

    return token
  } catch (error) {
    console.error('❌ Error getting auth token:', error)
    return null
  }
}

// Create axios instance with interceptors
const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
      'X-Client-Info': 'ioag-frontend/1.0',
      'X-Client-Type': 'web',
    },
  })

  // Request interceptor - inject Bearer token
  instance.interceptors.request.use(
    (config) => {
      const token = getValidToken()
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  // Response interceptor - handle errors globally
  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (error.response) {
        const { status, data } = error.response
        const apiError: ApiError = {
          code: 'HTTP_ERROR',
          message: error.message,
          statusCode: status,
          details: data,
        }

        // Handle specific status codes
        switch (status) {
          case 400: {
            apiError.code = 'BAD_REQUEST'
            apiError.message = 'Bad Request: Invalid request parameters'
            // The API returns error as { code, message }, not a string. Interpolating
            // the object straight into the template rendered "Bad Request: [object
            // Object]" and threw away the only useful part -- which is how a failing
            // save could look like a silently reverting form.
            const detail = (data as any)?.error
            const detailMessage =
              typeof detail === 'string' ? detail : typeof detail?.message === 'string' ? detail.message : undefined
            if (detailMessage) {
              apiError.message = `Bad Request: ${detailMessage}`
            }
            break
          }
          case 401: {
            apiError.code = 'UNAUTHORIZED'
            apiError.message = 'Authentication required or invalid token'
            const clientError = new ApiClientError(apiError, status)
            return handle401Error(error, instance, clientError)
          }
          case 403: {
            apiError.code = 'FORBIDDEN'
            apiError.message = 'Access denied: Insufficient permissions'
            // The partner/session endpoint is an intentional probe used to
            // detect partner role; a 403 here is the expected "not a partner"
            // signal, not a user-facing error. Suppress the global toast.
            const forbiddenUrl = error.config?.url || ''
            if (!forbiddenUrl.includes('/partner/session')) {
              emitAuthError('auth:forbidden', {
                statusCode: status,
                message: apiError.message,
                url: forbiddenUrl,
              })
            }
            break
          }
          case 404:
            apiError.code = 'NOT_FOUND'
            apiError.message = 'Resource not found'
            // Emit auth:deleted event only for account/profile endpoints
            const url = error.config?.url || ''
            if (url.includes('/accounts') || url.includes('/profile')) {
              emitAuthError('auth:deleted', {
                statusCode: status,
                message: 'Account or profile not found',
                url,
              })
            }
            break
          case 429:
            apiError.code = 'RATE_LIMIT'
            apiError.message = 'Too many requests: Rate limit exceeded'
            break
          case 500:
          case 502:
          case 503:
          case 504: {
            apiError.code = 'SERVER_ERROR'
            // Prefer the reason the API actually gave. Blanket-replacing every
            // 5xx with a generic string discarded messages the backend had
            // deliberately surfaced -- the admin "verify the SendGrid API key"
            // test reported "Internal server error" for six weeks while the
            // 502 it returned carried SendGrid's real rejection reason.
            const serverError = (data as any)?.error
            const serverMessage =
              typeof serverError === 'string'
                ? serverError
                : typeof serverError?.message === 'string'
                ? serverError.message
                : undefined
            apiError.message = serverMessage || 'Internal server error: Please try again later'
            break
          }
          default:
            apiError.message = `HTTP ${status}: ${error.message}`
        }

        throw new ApiClientError(apiError, status)
      } else if (error.request) {
        // Request made but no response received
        const apiError: ApiError = {
          code: 'NETWORK_ERROR',
          message: 'Network error: Unable to reach the server',
          details: error.message,
        }
        throw new ApiClientError(apiError)
      } else {
        // Error setting up the request
        const apiError: ApiError = {
          code: 'REQUEST_ERROR',
          message: `Request setup error: ${error.message}`,
          details: error,
        }
        throw new ApiClientError(apiError)
      }
    }
  )

  return instance
}

// Create singleton instance
const axiosInstance = createAxiosInstance()

// Public API requests (no authentication required)
// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
export async function apiRequestPublic(method: Method, url: string, data?: any): Promise<any> {
  const response = await axios({
    url: API_BASE_URL + url,
    method,
    data,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  return response.data
}

// Protected API requests (authentication required)
// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
export async function apiRequestProtected(method: Method, url: string, data?: any): Promise<any> {
  // Check for valid token before making request
  const token = getValidToken()

  if (!token) {
    throw new ApiClientError({
      code: 'UNAUTHORIZED',
      message: 'Authentication required: No valid token found',
      statusCode: 401,
    })
  }

  try {
    const response = await axiosInstance({
      url,
      method,
      data,
    })

    return response.data
  } catch (error) {
    // Re-throw ApiClientError instances
    if (error instanceof ApiClientError) {
      throw error
    }

    // Wrap other errors
    throw new ApiClientError({
      code: 'REQUEST_ERROR',
      message: error instanceof Error ? error.message : 'Unknown error occurred',
      details: error,
    })
  }
}

// Export axios instance for direct use if needed
export { axiosInstance }
